import { Component, OnInit, Injector, Input, ChangeDetectionStrategy } from "@angular/core";
import { environment } from "src/environments/environment";
import { GenericBasicGUI } from "src/model/class/component classes/generic-basic-gui";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { Supervision } from "src/model/class/general classes/supervision";
import { HAlign } from "src/model/enum/enumCustomInterface";
import { LogLevel } from "src/components/shared-components/enum/enumLog";

@Component({
    selector: "app-supervision-button",
    templateUrl: "./supervision-button.component.html",
    styleUrls: ["./supervision-button.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SupervisionButtonComponent
  extends GenericBasicGUI
  implements OnInit {
  _logContext: string = "Supervision-button.component";
  private _isPreviousSupervisionTheSame: boolean = false;

  public canUserShowThisButton: boolean = false;

  public styles = {
    "font-family": "Arial",
    // tslint:disable-next-line: object-literal-key-quotes
    color: "#000000",
    "font-size": "15px",
    "font-style": "normal", // italic
    "font-weight": "normal", // bol
    "text-decoration": "none", // underline
    "background-image": "",
    overflow: "hidden",
    display: "flex",
  };

  public stylesChild = {};

  /**
   * Giorgia -
   * @param injector The injector param is used by the parent classes to inject the services that they needed:
   * using this trick if the parent classes needed some specific service I don't need to inject them here
   * but I can do the injection in the specific class.
   */
  constructor(injector: Injector) {
    super(injector);
  }

  ngOnInit() {
    this.canUserShowSupervisionButton();

    /** Reassign hAlignment value if not passed as component parameter */
    if (!this.hAlignment) {
      this.hAlignment = HAlign.CENTER;
    }

    if (this.canUserShowThisButton) {
      [this.styles, this.stylesChild] = super.HandleProperties(
        this.styles,
        this.stylesChild
      );
      this.AssignFlexAlignment(
        this.styles,
        this.ComponentAPI.properties["HAlignment"],
        this.ComponentAPI.properties["VAlignment"]
      );

      this.styles["margin"] = this.assignComponentMarginByAlignment(
        this.hAlignment.toString()
      );
    }
  }

  ngOnDestroy() {
    this.onDestroyChildren();
  }

  protected setCurrentImage(urlImage: string, stretch: boolean) {
    if (!this.ShowingInDesktopVersion) {
      this.styles["width"] = "48px";
      this.styles["height"] = "48px";
      this.styles["background-size"] = "cover";
    }

    if (urlImage) {
      this.styles["background-image"] = "url(" + urlImage + ")";

      if (stretch.toString().toLowerCase() == "true") {
        this.styles["background-size"] = "contain";
      } else {
        this.styles["background-size"] = "initial";
      }

      this.styles["background-position"] = "center";
      this.styles["background-repeat"] = "no-repeat";
    } else {
      this.styles["background"] =
        AppSettings.CUSTOM_INTERFACE_BUTTON_DEFAULT_BACKGROUND;
      this.styles["border"] =
        AppSettings.CUSTOM_INTERFACE_BUTTON_DEFAULT_BORDER;
    }
  }

  /**
   * Mouse down event handler from desktop/web
   * If click action is different to "No action" set pressed image
   * if there is a longPressAction start its timer
   *
   * IF the "ion-content" element in which the button is contained is currently scrolling, NO ACTION IS EXECUTED.
   */
  public MouseDown(): void {
    // otherwise on mobile device the command is send 2 times
    if (
      !this.platform.is("ios") &&
      !this.platform.is("android") &&
      !this.isContainingListScrolling
    ) {
      this.handleStartPressedAction();
    }
  }

  /**
   * GF: handle the touch from device
   * if the button is keep pressed (not just a rapid touch), the event MouseUp and Click is not sended.
   * if there is a longPressAction start its timer.
   *
   * IF the "ion-content" element in which the button is contained is currently scrolling, NO ACTION IS EXECUTED.
   */
  public TouchStart(): void {
    if (!this.isContainingListScrolling) {
      this.handleStartPressedAction();
    }
  }

  /**
   * Mouse up event handler on desktop/web
   * If click action is different to "No action" set pressed image.
   *
   * IF the "ion-content" element in which the button is contained is currently scrolling, NO ACTION IS EXECUTED. ALSO, the associated image is un-pressed.
   */
  public MouseUp(): void {
    if (!this.platform.is("ios") && !this.platform.is("android")) {
      if (!this.isContainingListScrolling) {
        this.handleEndPressedAction();
      } else {
        this.SetPressedImage(false);
        this.cssCustomComponetClass = "";
      }
    }
  }

  /**
   * Mouse up event handler on device
   * If click action is different to "No action" set pressed image.
   *
   * IF the "ion-content" element in which the button is contained is currently scrolling, NO ACTION IS EXECUTED. ALSO, the associated image is un-pressed.
   */
  public TouchEnd(): void {
    if (!this.isContainingListScrolling) {
      this.handleEndPressedAction();
    } else {
      this.cssCustomComponetClass = "";
      this.SetPressedImage(false);
    }
  }

  public Click(): void { }

  /**
   * Navigate to clicked supervision
   */
  public onClickSupervisionButtonLink(event: Event): void { }

  private handleStartPressedAction(): void {
    this.cssCustomComponetClass = "clicked-supervision-button-element";

    this.SetPressedImage(true);
  }

  private async handleEndPressedAction(): Promise<void> {
    let canExecuteCommand: boolean = true;
    this.cssCustomComponetClass = "";

    this.SetPressedImage(false);

    canExecuteCommand =
      await this.handleOpenPopupRequestConfirmCommandExecution();

    if (canExecuteCommand) {
      this.navigateToSupervision();
    }
  }

  /**
   * Obtain if user could show the button.
   * It return falsy valuse if:
   * - User couln't show clicked supervision
   * - Clicked supervision does'nt exist
   */
  private canUserShowSupervisionButton(): void {
    const currentSupervisionAsArray: Array<string> =
      this.ComponentAPI.properties["Supervision"].split("|", 2);

    if (currentSupervisionAsArray.length > 0) {
      const linkedSupervision = parseInt(currentSupervisionAsArray[0], 10);

      //Peculiar case; the Supervision Button has been linked to a generic "Previously displayed Supervision"
      if (linkedSupervision.toString() === "-99") {
        const ionRouterElement = document.getElementsByTagName(
          "ion-router-outlet"
        )[0] as HTMLElement;

        const lastChild = ionRouterElement.children[
          ionRouterElement.children.length - 1
        ] as HTMLElement;
        const secondLastChild = ionRouterElement.children[
          ionRouterElement.children.length - 2
        ] as HTMLElement;
        if (
          secondLastChild?.tagName.toLowerCase() ===
          "app-supervision-graphics" ||
          secondLastChild?.tagName.toLowerCase() === "app-supervision-list"
        ) {
          this.canUserShowThisButton = true;

          //NOTE: Currently the peculiar case where the "secondLastChild" is constituted by the SAME SUPERVISION presented in "lastChild", but displayed
          //in a different mode (IE. graphical-mode VS list-mode) IS NOT HANDLED.
          /*
                    if (secondLastChild.tagName === lastChild.tagName) {
                      this.canUserShowThisButton = true;
                    } else {
                      const thirdLastChild = ionRouterElement.children[ionRouterElement.children.length - 3] as HTMLElement;
                      if (thirdLastChild?.tagName.toLowerCase() === "app-supervision-graphics" || thirdLastChild?.tagName.toLowerCase() === "app-supervision-list") {
                        this.canUserShowThisButton = true;
                        this._isPreviousSupervisionTheSame = true;
                      }
                    }
          */
        }
      } else {
        this.canUserShowThisButton =
          this.listSupervision.find(
            (supervision: Supervision) => supervision.id === linkedSupervision
          ) !== undefined;
      }
    }
  }
}
